import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPriceWithEuro } from '../../../utils/StringUtils';
import { calculateTotalToPay } from '../../../utils/order/orderUtils';

const getTvaRate = (tvaCode) => {
  if (!tvaCode) return 0;
  const rate = parseFloat(String(tvaCode).replace(',', '.'));
  return isNaN(rate) ? 0 : rate;
};

const arrondirDeuxDecimales = (nombre) => {
  const nombreStr = nombre.toFixed(3);
  const partieEntiere = nombreStr.split('.')[0];
  const partieDecimale = nombreStr.split('.')[1] || '000';
  const troisiemeDecimale = Number.parseInt(partieDecimale[2]);

  let entier = Number.parseInt(partieEntiere);
  let decimale = Number.parseInt(partieDecimale.slice(0, 2));

  if (troisiemeDecimale >= 5) {
    decimale += 1;
    if (decimale === 100) {
      entier += 1;
      decimale = 0;
    }
  }

  return Number.parseFloat(`${entier}.${decimale.toString().padStart(2, '0')}`).toFixed(2);
};

const calculateTvaTotals = (items) => {
  const tvaTotals = {};
  for (const item of items) {
    const itemPrice = item.promoPrice || item.price;
    const rate = getTvaRate(item.tvaCode);
    const total = item.Qty * itemPrice;
    if (rate > 0) {
      tvaTotals[rate] = (tvaTotals[rate] || 0) + total;
    }
  }
  return tvaTotals;
};

const formatTvaRate = (rate) => {
  return rate % 1 === 0 ? `${rate},00%` : `${rate.toFixed(2).replace('.', ',')}%`;
};

const generateInvoice = (invoiceData) => {
  const store = invoiceData.store;
  const cartItems = invoiceData.cartItems;
  const user = invoiceData.user;
  const totalPrice = calculateTotalToPay(cartItems.itemsPrice, cartItems.promoPrice, cartItems?.jackpot?.amount);
  const tvaResult = calculateTvaTotals(cartItems.orderItems);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 14;

  // --- Logo ---
  if (store.logo) {
    try {
      const logoWidth = 60;
      const logoHeight = 20;
      const logoX = (pageWidth - logoWidth) / 2;
      doc.addImage(store.logo, 'PNG', logoX, y, logoWidth, logoHeight);
      y += logoHeight + 15;
    } catch (e) {
      console.error('Logo loading failed:', e);
      y += 10;
    }
  }

  // --- Titre ---
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Facture simplifiée', pageWidth / 2, y, { align: 'center' });
  y += 14;

  // --- Infos magasin ---
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`Nom : ${store.name || ''}`, 14, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('Raison Sociale : ', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text('SUDICIT', 14 + doc.getTextWidth('Raison Sociale :   '), y);
  y += 6;
  if (store.address) {
    doc.setFont('helvetica', 'bold');
    doc.text('Adresse : ', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(store.address, 14 + doc.getTextWidth('Adresse :  '), y);
    y += 6;
  }
  doc.setFont('helvetica', 'bold');
  doc.text('SIRET : ', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text('92488712800024', 14 + doc.getTextWidth('SIRET : '), y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('RCS : ', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text('924 887 128 R.C.S. Nanterre', 14 + doc.getTextWidth('RCS : '), y);
  y += 6;

  // --- Infos client ---
  const paidDate = new Date(cartItems?.paidAt);
  const dateStr = paidDate.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
  const heureStr = paidDate.toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
  const clientEmail = cartItems?.userEmail || '';

  doc.setFont('helvetica', 'normal');
  const clientInfo = cartItems?.userEmail || user?.email || user?.phoneNumber || '';
doc.text(`Client : ${clientInfo}`, 14, y);
y += 6;
  doc.text('Date de l\'achat : ', 14, y);
  doc.setFont('helvetica', 'bold');
  doc.text(dateStr, 14 + doc.getTextWidth('Date de l\'achat : '), y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.text('Heure : ', 14, y);
  doc.setFont('helvetica', 'bold');
  doc.text(heureStr, 14 + doc.getTextWidth('Heure : '), y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.text('Lieu : ', 14, y);
  doc.setFont('helvetica', 'bold');
  doc.text(store.name || '', 14 + doc.getTextWidth('Lieu : '), y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.text('Moyen de paiement : ', 14, y);
  doc.setFont('helvetica', 'bold');
  doc.text(`${cartItems.paymentMethod || 'Carte bancaire'} (via PikkoPay)`, 14 + doc.getTextWidth('Moyen de paiement : '), y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.text('Référence de la transaction : ', 14, y);
  doc.setFont('helvetica', 'bold');
  doc.text(cartItems._id || '', 14 + doc.getTextWidth('Référence de la transaction : '), y);
  y += 14;

  // --- Tableau articles ---
  doc.setFont('helvetica', 'normal');
  autoTable(doc, {
    startY: y,
    head: [['Article', 'Qte', 'Prix unitaire', 'TVA', 'Total']],
    body: cartItems.orderItems.map((item) => {
      const unitPrice = item.promoPrice || item.price;
      const total = item.Qty * unitPrice;
      const tvaRate = getTvaRate(item.tvaCode);
      return [
        item.name,
        item.Qty,
        formatPriceWithEuro(unitPrice),
        formatTvaRate(tvaRate),
        tvaRate > 0 ? formatTvaRate(tvaRate) : 'N/C',
        formatPriceWithEuro(total),
      ];
    }),
    styles: { fontSize: 10 },
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { halign: 'center', cellWidth: 20 },
      2: { halign: 'right', cellWidth: 30 },
      3: { halign: 'center', cellWidth: 25 },
      4: { halign: 'right', cellWidth: 30 },
    },
  });

  y = doc.autoTable.previous.finalY + 10;

  // --- Total TTC ---
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total TTC : ${formatPriceWithEuro(totalPrice)}`, 14, y);
  y += 10;

  // --- Total TVA ---
  let totalTva = 0;
  const tvaLines = [];

  for (const [rate, montantTTC] of Object.entries(tvaResult)) {
    const rateNum = parseFloat(rate);
    const tvaAmount = parseFloat(arrondirDeuxDecimales((montantTTC * rateNum) / (100 + rateNum)));
    totalTva += tvaAmount;
    tvaLines.push({ rate: rateNum, amount: tvaAmount });
  }

  doc.text(`Total TVA : ${formatPriceWithEuro(totalTva.toFixed(2))}`, 14, y);
  y += 6;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  for (const line of tvaLines) {
    doc.text(`TVA à ${formatTvaRate(line.rate)} : ${formatPriceWithEuro(line.amount.toFixed(2))}`, 14, y);
    y += 6;
  }

  // --- Output ---
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
};

export default generateInvoice;
